import { useState } from 'react';
import './ProductPreview.css';

function ProductPreview({ isOpen, onClose, productData }) {
  const [previewMode, setPreviewMode] = useState('desktop'); // 'mobile', 'tablet', 'desktop'

  if (!isOpen) return null;

  // Extract data from productData
  const {
    name = 'Product Name',
    sku = 'SKU-000',
    price = 0,
    compareAtPrice,
    description = '',
    shortDescription = '',
    images = [],
    status = 'DRAFT',
    stock = 0,
    isFeatured = false,
    category,
    features = [],
    tags = '',
    artist = '',
    dimensions = '',
    materials = '',
  } = productData;

  // Get primary image or first image
  const primaryImage = images.find(img => img.isPrimary) || images[0];
  const imageUrl = primaryImage?.url || primaryImage?.file
    ? (primaryImage.url || URL.createObjectURL(primaryImage.file))
    : null;

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  // Calculate discount percentage
  const discountPercentage = compareAtPrice && compareAtPrice > price
    ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
    : 0;

  // Parse tags
  const tagList = typeof tags === 'string'
    ? tags.split(',').map(t => t.trim()).filter(t => t)
    : Array.isArray(tags) ? tags : [];

  return (
    <div className="preview-overlay" onClick={onClose}>
      <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="preview-header">
          <div className="preview-title">
            <h3>Product Preview</h3>
            <span className="preview-status">{status}</span>
          </div>

          <div className="preview-controls">
            <div className="device-switcher">
              <button
                className={`device-btn ${previewMode === 'mobile' ? 'active' : ''}`}
                onClick={() => setPreviewMode('mobile')}
                title="Mobile View"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                </svg>
              </button>
              <button
                className={`device-btn ${previewMode === 'tablet' ? 'active' : ''}`}
                onClick={() => setPreviewMode('tablet')}
                title="Tablet View"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5h3m-6.75 2.25h10.5a2.25 2.25 0 002.25-2.25v-15a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 4.5v15a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </button>
              <button
                className={`device-btn ${previewMode === 'desktop' ? 'active' : ''}`}
                onClick={() => setPreviewMode('desktop')}
                title="Desktop View"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
                </svg>
              </button>
            </div>

            <button className="preview-close" onClick={onClose}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Preview Body */}
        <div className="preview-body">
          <div className={`preview-frame ${previewMode}`}>
            <div className="product-preview-card">
              {/* Image */}
              {imageUrl ? (
                <div className="preview-image">
                  <img src={imageUrl} alt={name} />
                  {isFeatured && (
                    <span className="featured-badge">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </span>
                  )}
                  {discountPercentage > 0 && (
                    <span className="discount-badge">-{discountPercentage}%</span>
                  )}
                  {stock <= 0 && (
                    <span className="stock-badge out-of-stock">Out of Stock</span>
                  )}
                  {stock > 0 && stock <= 5 && (
                    <span className="stock-badge low-stock">Only {stock} left</span>
                  )}
                </div>
              ) : (
                <div className="preview-image-placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  <span>No Image</span>
                </div>
              )}

              {/* Content */}
              <div className="preview-content">
                {category && <span className="preview-category">{category.name || category}</span>}

                <h2 className="preview-name">{name}</h2>

                {shortDescription && (
                  <p className="preview-short-desc">{shortDescription}</p>
                )}

                <div className="preview-price">
                  <span className="current-price">{formatPrice(price)}</span>
                  {compareAtPrice && compareAtPrice > price && (
                    <span className="compare-price">{formatPrice(compareAtPrice)}</span>
                  )}
                </div>

                {artist && (
                  <div className="preview-meta">
                    <strong>Artist:</strong> {artist}
                  </div>
                )}

                {dimensions && (
                  <div className="preview-meta">
                    <strong>Dimensions:</strong> {dimensions}
                  </div>
                )}

                {materials && (
                  <div className="preview-meta">
                    <strong>Materials:</strong> {materials}
                  </div>
                )}

                {description && (
                  <div className="preview-description">
                    <h3>Description</h3>
                    <div dangerouslySetInnerHTML={{ __html: description }} />
                  </div>
                )}

                {features.length > 0 && (
                  <div className="preview-features">
                    <h3>Features</h3>
                    <ul>
                      {features.map((feature, index) => (
                        <li key={index}>{typeof feature === 'string' ? feature : feature.text}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {tagList.length > 0 && (
                  <div className="preview-tags">
                    {tagList.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                )}

                <div className="preview-sku">SKU: {sku}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductPreview;
